import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Connector from '../../Components/Connector';
import Page from '../../Components/Page';
import IfRender from '../../Components/Utils/IfRender';
import CheckoutForm from '../../Components/Checkout';
import Button from '../../Components/Utils/Button';
import Login from '../../pages/Login';
import ModalFrame from '../../Components/Modals/ModalFrame';

import { CHARGING_STATES } from '../../Constants';

import { usePaymentIntentMutation } from '../../redux/api/payment';
import { useGetChargingUserAccessQuery } from '../../redux/api/users';
import { setConnector } from '../../redux/reducers/chargingPointSlice';
import { selectConnector } from '../../redux/selectors/chargingPoint';

import '../../Styles/style.scss';
import './style.scss';

const fonts = [{ cssSrc: 'https://fonts.googleapis.com/css?family=Poppins' }];

const ChargeOrPay = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cp_id, connector_id } = useParams();

  const connector = useSelector(selectConnector);

  const [clientSecret, setClientSecret] = useState('');
  const [isLoginRegisterOpened, setIsLoginRegisterOpened] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);

  const paymentAvailable = connector?.status === CHARGING_STATES.PREPARING;
  const [paymentIntent, { data: paymentIntentData, isSuccess: isPaymentIntentSuccess }] =
    usePaymentIntentMutation();
  const {
    data: { access: accessData = '', chargePoint: chargerData, connector: connectorData = {} } = {},
    refetch,
    isSuccess,
    isError,
    error: { data: { message: errorMessage = 'Error ', code: errorCode = '' } = {} } = {}
  } = useGetChargingUserAccessQuery({
    chargePointId: cp_id,
    chargePointConnectorId: connector_id
  });
  useEffect(() => {
    if (isSuccess) {
      dispatch(setConnector({ connector: connectorData }));
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isPaymentIntentSuccess) {
      setClientSecret(paymentIntentData.client_secret);
    }
  }, [isPaymentIntentSuccess]);

  useEffect(() => {
    setStripePromise(loadStripe(`${import.meta.env.VITE_STRIPE_KEY}`));
  }, []);

  useEffect(() => {
    if (paymentAvailable && isSuccess && connector?.chargingPoint?.prices?.length > 0) {
      paymentIntent({
        chargePointId: +cp_id,
        chargePointConnectorId: +connector_id
      });
    }
  }, [paymentAvailable, isSuccess, connector]);

  const fontSize =
    connector && connector instanceof Element
      ? window.getComputedStyle(connector).getPropertyValue('font-size')
      : '';
  const themeStyle =
    connector && connector instanceof Element
      ? window.getComputedStyle(connector.querySelector('#theme-provider'))
      : {};
  const bravoBackground =
    themeStyle && themeStyle instanceof Element
      ? themeStyle.getPropertyValue('--bravoBackground')
      : '';
  const alfaForeground =
    themeStyle && themeStyle instanceof Element
      ? themeStyle.getPropertyValue('--alfaForeground')
      : '';
  const charlieForeground =
    themeStyle && themeStyle instanceof Element
      ? themeStyle.getPropertyValue('--charlieForeground')
      : '';
  const deltaBackground =
    themeStyle && themeStyle instanceof Element
      ? themeStyle.getPropertyValue('--deltaBackground')
      : '';
  const alfaColor =
    themeStyle && themeStyle instanceof Element ? themeStyle.getPropertyValue('--alfaColor') : '';
  const bravoColor =
    themeStyle && themeStyle instanceof Element ? themeStyle.getPropertyValue('--bravoColor') : '';

  const appearance = {
    theme: 'stripe',
    labels: 'above',
    variables: {
      colorPrimary: alfaColor,
      colorBackground: bravoBackground,
      colorText: alfaForeground,
      colorDanger: bravoColor,
      fontFamily: 'Poppins, system-ui, sans-serif',
      fontSizeBase: fontSize,
      spacingUnit: '0.25rem',
      borderRadius: '0.75rem',
      fontLineHeight: '1.5',
      spacingGridRow: '0.625rem',
      fontSizeSm: '0.875rem'
    },
    rules: {
      '.Label': {
        color: charlieForeground,
        margin: '0 0 0.25rem 0.625rem',
        fontWeight: '600'
      },
      '.Input': {
        border: `0.125rem solid ${deltaBackground}`,
        padding: '0.625rem',
        // fontSize: '1rem',
        lineHeight: '1.5'
      }
    }
  };

  const options = {
    clientSecret: clientSecret,
    appearance,
    fonts
  };

  return (
    <Page
      title={`${t('charge_with')} ${cp_id}`}
      noSidebar={true}
      contentClass='page__content--center'>
      <IfRender condition={isLoginRegisterOpened}>
        <ModalFrame onClose={() => setIsLoginRegisterOpened(false)}>
          <Login
            onSuccess={() => {
              setIsLoginRegisterOpened(false);
              refetch();
            }}
            noRedirect
          />
        </ModalFrame>
      </IfRender>
      <IfRender condition={isError}>
        <div className='payment-page__error'>
          {errorMessage}
          <div className='payment-page__error payment-page__error__buttons'>
            <IfRender condition={errorCode === 403 && errorMessage.includes('Login')}>
              <Button
                className='payment-page__error__buttons__button'
                onClick={() => setIsLoginRegisterOpened(true)}>
                {t('login_or_register')}
              </Button>
            </IfRender>
            <IfRender condition={errorCode === 403 && !errorMessage.includes('Login')}>
              <Button
                className='payment-page__error__buttons__button'
                onClick={() => {
                  navigate('/dashboard');
                  Ï;
                }}>
                {t('go_to_dashboard')}
              </Button>
            </IfRender>
          </div>
        </div>
      </IfRender>
      <IfRender condition={connector && isSuccess}>
        <Connector
          connector={{ ...connector, chargingPoint: { ...chargerData } }}
          hideButton={!accessData.includes('charge')}
          fromCharge>
          {paymentAvailable && clientSecret && (
            <>
              <IfRender condition={accessData.length && accessData.includes('payment')}>
                <Elements stripe={stripePromise} options={options}>
                  <IfRender condition={accessData.length && accessData.includes('login')}>
                    <Button
                      className='payment-page__login'
                      onClick={() => setIsLoginRegisterOpened(true)}>
                      {t('login_or_register')}
                    </Button>
                  </IfRender>
                  {paymentIntentData && (
                    <h3 className='payment-page__authorize-amount'>
                      {t('authorize_amount')}:
                      <span>
                        {paymentIntentData.amount / 100} {paymentIntentData.currency}
                      </span>
                    </h3>
                  )}
                  <CheckoutForm
                    appearance={appearance}
                    paymentIntent={paymentIntentData}
                    cp_id={cp_id}
                    connector_id={connector_id}
                    onSuccess={() => setClientSecret('')}
                    clientSecret={clientSecret}
                    price={paymentIntentData?.amount}
                    currency={paymentIntentData?.currency}
                    fromCharge
                  />
                </Elements>
              </IfRender>
            </>
          )}
        </Connector>
      </IfRender>
    </Page>
  );
};

export default ChargeOrPay;
